import { useCallback } from 'react';
import { useDispatch , useSelector } from 'react-redux';
import { addMessage, toggleLoader } from '../store/actions';
import useCheckerManager from './useCheckerManager';
import { v4 as uuidv4 } from 'uuid';
import { storageKeys } from '../constants/storageKeys';

// Custom hook to execute BPMN checkers and dispatch results as messages.
export const useCheckers = () => {
  const dispatch = useDispatch();
  const { invokeAllSelectedCheckers  } = useCheckerManager(); 
  const historyPointer = useSelector(state => state.history.pointer);

  /**
   * Executes all selected BPMN checkers with the provided XML and JSON BPMN data.
   * Handles loader state, error reporting, and dispatches checker results as messages.
   *
   * @param {string} xml - The BPMN XML string to be checked.
   * @param {Object} jsonBPMN - The BPMN JSON object to be checked.
   */
  const runCheckers = useCallback(
    async (xml, jsonBPMN) => {
      dispatch(toggleLoader('checkerManager', true)); // Show loader

      if (!xml) {
        console.error('XML is not available for checker execution.');
        dispatch(toggleLoader('checkerManager', false)); // Hide loader
        return;
      }

      try {
        // Invoke selected checkers and get responses
        const checkerResponses = await invokeAllSelectedCheckers (xml, jsonBPMN);

        // If in /direct-render route, log responses; else, dispatch as messages
        if (window.location.pathname === '/pubserv/BPMN-Chatbot-Beta/direct-render') {
          alert('Checker responses are logged to the console.');
          console.log('Checker Responses:', checkerResponses);
        } else {
          checkerResponses.forEach((checkerResponse) => {
            processCheckerResponse(checkerResponse, historyPointer, dispatch);
          });
        }

      } catch (error) {
        dispatch(
          addMessage({
            sender: 'checkerManager',
            content: error,
          })
        );
      } finally {
        dispatch(toggleLoader('checkerManager', false)); // Hide loader
      }
    },
    [dispatch, invokeAllSelectedCheckers, historyPointer]
  );

  return runCheckers;
};

/**
 * Returns an icon representing the classification type.
 * @param {string} classification - The classification string (e.g., 'error', 'warning', 'pattern-error', 'pattern-warning').
 * @returns {string} - The corresponding icon for the classification, or an empty string if not recognized.
 */
const getClassificationIcon = (classification) => {
  if (!classification) return '';

  const upper = classification.toUpperCase();

  if (upper.includes('ERROR')) return '❌';
  if (upper.includes('WARNING')) return '⚠️';
  // Add more contains-based rules if needed

  return '';
};

/**
 * Processes a single checker response and dispatches appropriate messages to the Redux store.
 *
 * - If the response contains results, it dispatches a message for each result, including icons, autofix, and highlight options.
 * - If the response is empty, it dispatches a "No issues found" message.
 * - If there is an error, it dispatches an error message.
 * - Stores each checker result in sessionStorage for later retrieval (e.g., for copy/autofix/highlight actions).
 *
 * @param {Object} checkerResponse - The response object from a checker, containing either a 'response' array or an 'error'.
 * @param {Function} dispatch - The Redux dispatch function to send messages to the store.
 */
function processCheckerResponse(checkerResponse, historyPointer, dispatch) {
  if (checkerResponse.response) {
    if (checkerResponse.response.length === 0) {
      // Handle case where response is an empty array
      dispatch(
        addMessage({
          sender: 'checkerManager',
          content: `✅ ${checkerResponse.checkerName}: No issues found.`,
        })
      );
    } else {
      // Get SHOW_CHECKERS_WARNINGS from localStorage (default to true)
      const showCheckersWarnings = JSON.parse(localStorage.getItem(storageKeys.SHOW_CHECKERS_WARNINGS) ?? 'true');

      // Handle non-empty response array
      checkerResponse.response.forEach((checkerResult) => {
        // If SHOW_CHECKERS_WARNINGS is false and classification includes 'warning', skip this result
        if (
          showCheckersWarnings === false &&
          checkerResult.classification &&
          checkerResult.classification.toUpperCase().includes('WARNING') 
        ) {
          return; // skip this warning
        }

        const checkerResultId = uuidv4();

        // Retrieve the current mapping from session storage or initialize an empty object
        const checkerResultsMapping = JSON.parse(sessionStorage.getItem(storageKeys.CHECKERS_RESULTS_MAPPING)) || {};

        // Add the new mapping
        checkerResultsMapping[checkerResultId] = checkerResult;

        // Save the updated mapping back to session storage
        sessionStorage.setItem(storageKeys.CHECKERS_RESULTS_MAPPING, JSON.stringify(checkerResultsMapping));
        
        const messageContent = checkerResult.message
        ? ` <b>${getClassificationIcon(checkerResult.classification)} ${checkerResponse.checkerName}: ${checkerResult.classification}</b>
            <br/>${checkerResult.message}
            <br/><span class='copy-me' data-id='${checkerResultId}'>[Copy]</span>
             ${
              checkerResult.promptInstructions || checkerResult.autofixMessage
                ? `<br/><span class='autofix-me' data-id='${checkerResultId}' data-pointer='${historyPointer}'>[Autofix]</span>`
                : ''
            }
            ${checkerResult.paths ? `<br/><span class='highlight-path' data-id='${checkerResultId}' data-pointer='${historyPointer}'>[Highlight Elements]</span>` : ''}
            <br/> <span class='go-to-model-diagram' data-id='${historyPointer}'>[View Diagram]</span>
            `
        : `${checkerResponse.checkerName}: No issues found.`;

        dispatch(
          addMessage({
            sender: 'checkerManager',
            content: messageContent,
          })
        );

      });
    }
  } else if (checkerResponse.error) {
    // Handle errors
    dispatch(
      addMessage({
        sender: 'checkerManager',
        content: `Error from ${checkerResponse.checkerName}: ${checkerResponse.error}`,
      })
    );
  }
}
